import React, { useEffect, useState } from "react";
import axios from "axios";
import { Row, Col, Spin, Empty, Form, Modal, notification } from "antd";
import { useSelector } from "react-redux";
import styled from "styled-components";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import GridOnIcon from "@mui/icons-material/GridOn";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import ImageIcon from "@mui/icons-material/Image";
import { DownloadOutlined, LoadingOutlined, DeleteOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import Button from "atoms/Button";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { getUserInfo, getMenuList, getUserRoleId } from "redux/selectors";
import AddClientDocument from "./AddClientDocument";
import { hasClientPermission } from "./clientPermission";

const PDFJS_VERSION = "3.11.174";
const PDFJS_SCRIPT_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const PDFJS_WORKER_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;

let pdfjsLoaderPromise = null;

// Loads pdf.js from cdnjs once and caches the promise so every PDF thumbnail
// on the page shares the same load instead of injecting the script repeatedly.
const loadPdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfjsLoaderPromise) return pdfjsLoaderPromise;

  pdfjsLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = PDFJS_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
        resolve(window.pdfjsLib);
      } else {
        reject(new Error("pdf.js failed to load"));
      }
    };
    script.onerror = () => reject(new Error("pdf.js failed to load"));
    document.body.appendChild(script);
  });

  return pdfjsLoaderPromise;
};

const StyledWrap = styled.div`
  .doc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .doc-count {
    font-size: 14px;
    color: #444;
    font-weight: 500;
  }

  .doc-card {
    background: #fff;
    border: 1px solid #eee;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: box-shadow 0.2s ease, transform 0.2s ease;
    margin-bottom: 24px;

    &:hover {
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }
  }

  .doc-thumb {
    height: 160px;
    background: #f5f6f8;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
  }

  .doc-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .doc-thumb canvas {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  }

  .sheet-preview {
    width: 100%;
    height: 100%;
    padding: 8px;
    overflow: hidden;
    background: #fff;
  }

  .sheet-preview table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  .sheet-preview td {
    border: 1px solid #e6e6e6;
    font-size: 8px;
    padding: 2px 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #333;
  }

  .sheet-preview tr:first-child td {
    background: #f2f6ff;
    font-weight: 600;
  }

  .doc-thumb .play-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.15);
  }

  .download-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 26px;
    height: 30px;
    border-radius: 6px;
    background: #C2FAE5;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transform: translateY(-4px);
    transition: opacity 0.15s ease, transform 0.15s ease;
    z-index: 2;
  }

  .doc-card:hover .download-btn {
    opacity: 1;
    transform: translateY(0);
  }

  .download-btn:hover {
    background: #C2FAE5;
  }

  .download-btn svg {
    font-size: 18px;
    color: #000;
  }

  .delete-btn {
    position: absolute;
    top: 8px;
    left: 8px;
    width: 26px;
    height: 30px;
    border-radius: 6px;
    background: #fde2e1;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transform: translateY(-4px);
    transition: opacity 0.15s ease, transform 0.15s ease;
    z-index: 2;
  }

  .doc-card:hover .delete-btn {
    opacity: 1;
    transform: translateY(0);
  }

  .delete-btn:hover {
    background: #fde2e1;
  }

  .delete-btn svg {
    font-size: 18px;
  }

  .doc-name {
    padding: 10px 12px;
    font-size: 14px;
    color: #1b1b1b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .state-box {
    padding: 60px 0;
    text-align: center;
  }
`;

// "id" is only unique within its own source table (clientportal vs owncrm),
// so the two unioned lists can hand back collidng ids — key/track rows by
// source+id instead of id alone.
const getDocKey = (doc) => `${doc.source || "x"}-${doc.id}`;

const getFileKind = (filetype = "", filename = "") => {
  const type = filetype.toLowerCase();
  const ext = filename.split(".").pop()?.toLowerCase() || "";

  if (type.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) {
    return "image";
  }
  if (type === "image/svg+xml" || ext === "svg") return "svg";
  if (type.startsWith("video/") || ["mp4", "mov", "avi", "webm"].includes(ext)) {
    return "video";
  }
  if (type === "application/pdf" || ext === "pdf") return "pdf";
  if (
    type.includes("spreadsheet") ||
    ["xlsx", "xls", "csv"].includes(ext)
  ) {
    return "sheet";
  }
  if (
    type.includes("word") ||
    type === "application/msword" ||
    ["doc", "docx"].includes(ext)
  ) {
    return "doc";
  }
  return "file";
};

const DownloadButton = ({ downloading, onClick }) => (
  <div
    className="download-btn"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    title="Download"
  >
    {downloading ? (
      <LoadingOutlined style={{ fontSize: 16, color: "#000" }} spin />
    ) : (
      <DownloadOutlined style={{ fontSize: 16, color: "#000" }} />
    )}
  </div>
);

const DeleteButton = ({ deleting, onClick }) => (
  <div
    className="delete-btn"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    title="Delete"
  >
    {deleting ? (
      <LoadingOutlined style={{ fontSize: 16, color: "#000" }} spin />
    ) : (
      <DeleteOutlined style={{ fontSize: 16, color: "#c0392b" }} />
    )}
  </div>
);

const FallbackIcon = ({ kind }) => {
  const iconMap = {
    pdf: <PictureAsPdfIcon style={{ fontSize: 48, color: "#e74c3c" }} />,
    sheet: <GridOnIcon style={{ fontSize: 48, color: "#1e7145" }} />,
    doc: <DescriptionIcon style={{ fontSize: 48, color: "#2b579a" }} />,
    file: <InsertDriveFileIcon style={{ fontSize: 48, color: "#8c8c8c" }} />,
  };
  return iconMap[kind] || iconMap.file;
};

// Renders the first page of a PDF onto a canvas using pdf.js.
const PdfThumb = ({ doc }) => {
  const canvasRef = React.useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let cancelled = false;

    const render = async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        const res = await axios.get(doc.publicurl, { responseType: "arraybuffer" });
        if (cancelled) return;

        const pdf = await pdfjsLib.getDocument({ data: res.data }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });
        const scale = 220 / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        await page.render({
          canvasContext: canvas.getContext("2d"),
          viewport: scaledViewport,
        }).promise;

        if (!cancelled) setStatus("ready");
      } catch (err) {
        if (!cancelled) setStatus("error");
      }
    };

    render();
    return () => {
      cancelled = true;
    };
  }, [doc.publicurl]);

  return (
    <>
      {status !== "ready" && (
        <>{status === "loading" ? <Spin size="small" /> : <FallbackIcon kind="pdf" />}</>
      )}
      <canvas ref={canvasRef} style={{ display: status === "ready" ? "block" : "none" }} />
    </>
  );
};

// Parses the first sheet of an Excel/CSV file and renders a small table snippet.
const SheetThumb = ({ doc }) => {
  const [rows, setRows] = useState(null); // null = loading, [] = error/empty
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;

    const render = async () => {
      try {
        const res = await axios.get(doc.publicurl, { responseType: "arraybuffer" });
        if (cancelled) return;

        const workbook = XLSX.read(res.data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheetName];
        const data = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          range: 0,
          blankrows: false,
        });

        const preview = data.slice(0, 6).map((row) => row.slice(0, 4));
        if (!cancelled) {
          setRows(preview);
          setStatus(preview.length ? "ready" : "error");
        }
      } catch (err) {
        if (!cancelled) setStatus("error");
      }
    };

    render();
    return () => {
      cancelled = true;
    };
  }, [doc.publicurl]);

  if (status === "loading") return <Spin size="small" />;
  if (status === "error") return <FallbackIcon kind="sheet" />;

  return (
    <div className="sheet-preview">
      <table>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell ?? ""}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DocThumb = ({ doc, downloading, onDownload, deleting, onDelete, canDelete }) => {
  const kind = getFileKind(doc.filetype, doc.filename);

  const actions = (
    <>
      <DownloadButton downloading={downloading} onClick={onDownload} />
      {canDelete && <DeleteButton deleting={deleting} onClick={onDelete} />}
    </>
  );

  if (kind === "image" || kind === "svg") {
    return (
      <div className="doc-thumb">
        <img src={doc.publicurl} alt={doc.filename} loading="lazy" />
        {actions}
      </div>
    );
  }

  if (kind === "video") {
    return (
      <div className="doc-thumb">
        <img src={doc.thumbnailurl || doc.publicurl} alt={doc.filename} loading="lazy" />
        <div className="play-overlay">
          <PlayCircleFilledIcon style={{ fontSize: 40, color: "#fff" }} />
        </div>
        {actions}
      </div>
    );
  }

  if (kind === "pdf") {
    return (
      <div className="doc-thumb">
        <PdfThumb doc={doc} />
        {actions}
      </div>
    );
  }

  if (kind === "sheet") {
    return (
      <div className="doc-thumb">
        <SheetThumb doc={doc} />
        {actions}
      </div>
    );
  }

  return (
    <div className="doc-thumb">
      <FallbackIcon kind={kind} />
      {actions}
    </div>
  );
};

const ClientDocuments = ({ filteredData }) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [form] = Form.useForm();

  const userInfo = useSelector((state) => getUserInfo(state));
  const menuList = useSelector((state) => getMenuList(state));
  const userRoleId = useSelector((state) => getUserRoleId(state));
  const Menulist = menuList?.[userRoleId] || Object.values(menuList || {})[0] || [];
  const canAddDocuments = hasClientPermission(Menulist, "create");
  const canDeleteDocuments = hasClientPermission(Menulist, "delete");
  const clientId = filteredData?.bdo_id;

  const fetchDocuments = async () => {
    if (!clientId) return;
    setIsLoading(true);
    setError(null);
    try {
      const { code, data, message } = await CALL_API(
        `insyts-crm-clients/getdocuments?clientid=${clientId}`,
        "GET",
      );
      if (code === STATUS_CODE.SUCCESS) {
        setDocuments(data || []);
      } else {
        setError(message || "Failed to fetch documents");
      }
    } catch (err) {
      setError(err?.message || "Failed to fetch documents");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const closeAddModal = () => {
    setShowAddModal(false);
    setFile(null);
    form.resetFields();
  };

  // Direct-to-S3 upload: same 3-step flow as IdentityDocUpload/useS3upload —
  // (1) ask the backend for a presigned PUT url, (2) PUT the file straight to
  // S3 from the browser, (3) register the resulting object key against the
  // client so it shows up in the list.
  const handleAddDocument = async () => {
    if (!canAddDocuments) return;
    if (!file) {
      notification.error({ message: "Please upload a file." });
      return;
    }

    setIsUploading(true);
    try {
      const { code: urlCode, url, key } = await CALL_API("insyts-crm-clients/getuploadurl", "post", {
        clientid: clientId,
        filename: file.name,
        filetype: file.type,
      });

      if (urlCode !== STATUS_CODE.SUCCESS || !url) {
        notification.error({ message: "Could not start the upload." });
        return;
      }

      const putRes = await axios.put(url, file, {
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });

      if (putRes.status < 200 || putRes.status >= 300) {
        notification.error({ message: "File upload failed" });
        return;
      }

      const { code, message } = await CALL_API("insyts-crm-clients/adddocument", "post", {
        clientid: clientId,
        filename: file.name,
        filetype: file.type,
        filesize: file.size,
        objectKey: key,
        userid: userInfo?.userid,
      });

      if (code === STATUS_CODE.SUCCESS) {
        notification.success({ message: "Document added successfully" });
        closeAddModal();
        fetchDocuments();
      } else {
        notification.error({ message: message || "Failed to upload document" });
      }
    } catch (err) {
      notification.error({ message: "Failed to upload document" });
    } finally {
      setIsUploading(false);
    }
  };

  const [downloadingIds, setDownloadingIds] = useState({});

  const handleOpen = (doc) => {
    window.open(doc.publicurl, "_blank", "noopener,noreferrer");
  };

  const handleDownload = async (doc) => {
    const docKey = getDocKey(doc);
    if (downloadingIds[docKey]) return;
    setDownloadingIds((prev) => ({ ...prev, [docKey]: true }));

    try {
      const res = await axios.get(doc.publicurl, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", doc.filename || "download");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // Fallback: if the file can't be fetched as a blob (e.g. CORS), open it directly
      window.open(doc.publicurl, "_blank", "noopener,noreferrer");
    } finally {
      setDownloadingIds((prev) => ({ ...prev, [docKey]: false }));
    }
  };

  const [deletingIds, setDeletingIds] = useState({});

  // Soft-delete only: the row stays in the table with statusid flipped to 0
  // (see deleteClientDocument on the backend), so nothing here ever removes
  // a record — it just stops showing up once removed from this list.
  const handleDelete = (doc) => {
    const docKey = getDocKey(doc);
    Modal.confirm({
      title: "Delete this document?",
      content: doc.filename,
      okText: "Yes",
      cancelText: "No",
      centered: true,
      onOk: async () => {
        setDeletingIds((prev) => ({ ...prev, [docKey]: true }));
        try {
          const { code, message } = await CALL_API(
            `insyts-crm-clients/deletedocument?documentid=${doc.id}&clientid=${clientId}`,
            "delete",
          );
          if (code === STATUS_CODE.SUCCESS) {
            notification.success({ message: "Document deleted" });
            setDocuments((prev) => prev.filter((d) => d !== doc));
          } else {
            notification.error({ message: message || "Failed to delete document" });
          }
        } catch (err) {
          notification.error({ message: "Failed to delete document" });
        } finally {
          setDeletingIds((prev) => ({ ...prev, [docKey]: false }));
        }
      },
    });
  };

  return (
    <StyledWrap>
      <div className="doc-header">
        <div className="doc-count">List of Documents: {documents.length}</div>
        {canAddDocuments && (
          <Button type="primary" onClick={() => setShowAddModal(true)}>
            Add Document
          </Button>
        )}
      </div>

      <AddClientDocument
        showAddModal={showAddModal}
        onClose={closeAddModal}
        onFinish={handleAddDocument}
        setFile={setFile}
        isUpdating={isUploading}
        form={form}
      />

      {isLoading ? (
        <div className="state-box">
          <Spin size="large" />
        </div>
      ) : error ? (
        <div className="state-box">
          <Empty description={error} />
        </div>
      ) : documents.length === 0 ? (
        <div className="state-box">
          <Empty description="No documents found" />
        </div>
      ) : (
        <Row gutter={24}>
          {documents.map((doc) => (
            <Col xs={24} sm={12} md={8} lg={6} key={getDocKey(doc)}>
              <div className="doc-card" onClick={() => handleOpen(doc)}>
                <DocThumb
                  doc={doc}
                  downloading={!!downloadingIds[getDocKey(doc)]}
                  onDownload={() => handleDownload(doc)}
                  deleting={!!deletingIds[getDocKey(doc)]}
                  onDelete={() => handleDelete(doc)}
                  canDelete={doc.source === "owncrm" && canDeleteDocuments}
                />
                <div className="doc-name" title={doc.filename}>
                  {doc.filename}
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}
    </StyledWrap>
  );
};

export default ClientDocuments;